import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2, KeyRound, Loader2, ShieldAlert } from "lucide-react";
import { motion } from "framer-motion";
import api, { formatApiError } from "../lib/api";
import { PasswordChecklist, isPasswordValid } from "../components/PasswordChecklist";
import { PasswordField } from "../components/PasswordField";
import { AuthHeroPanel } from "../components/AuthHeroPanel";

const E1_LOGO =
  "https://customer-assets-7cd3h4nn.emergentagent.net/job_err-dashboard-portal/artifacts/phsk29o5_E1-Primary-Logo_Small_Transparent.png";

export default function AcceptInvitePage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [invite, setInvite] = useState(null);
  const [checking, setChecking] = useState(Boolean(token));
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [complete, setComplete] = useState(false);
  const [error, setError] = useState(token ? "" : "This invitation link is incomplete.");

  useEffect(() => {
    if (!token) return;
    api
      .get(`/auth/invite/${token}`)
      .then(({ data }) => setInvite(data))
      .catch((e) =>
        setError(
          formatApiError(e.response?.data?.detail) ||
            "This invitation link is invalid or has expired."
        )
      )
      .finally(() => setChecking(false));
  }, [token]);

  const submit = async (event) => {
    event.preventDefault();
    if (newPassword.length < 12) {
      setError("Choose a password with at least 12 characters.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("The passwords do not match.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/accept-invite", { token, new_password: newPassword });
      setComplete(true);
    } catch (e) {
      setError(
        formatApiError(e.response?.data?.detail) ||
          "We could not activate your account. Ask your administrator to resend the invitation."
      );
    } finally {
      setLoading(false);
    }
  };

  const blocked = !invite && !checking;

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-5" data-testid="accept-invite-page">
      <AuthHeroPanel testId="accept-invite-panel" logoTestId="accept-invite-brand"
        eyebrow="Account activation"
        footer={<><KeyRound size={14} /> One-time link · Email verification at sign-in</>}>
        <h1 className="font-heading text-5xl font-light leading-none xl:text-6xl">
          Welcome aboard.<br />
          <span className="font-black">Set your password.</span>
        </h1>
      </AuthHeroPanel>

      <main className="col-span-1 flex items-center justify-center px-6 py-12 md:px-16 lg:col-span-3">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-md"
        >
          <img
            src={E1_LOGO}
            alt="EmployeesFirst"
            className="mb-10 h-12 max-w-[260px] w-auto object-contain lg:hidden"
            width="260"
            height="32"
            data-testid="accept-invite-mobile-logo"
          />
          <Link
            to="/login"
            className="mb-10 inline-flex items-center gap-2 text-sm font-semibold"
            style={{ color: "var(--ink)" }}
            data-testid="accept-invite-back-link"
          >
            <ArrowLeft size={16} /> Back To Sign In
          </Link>

          <div className="eyebrow mb-3">Activate your account</div>
          <h2
            className="font-heading text-4xl font-black leading-none md:text-5xl"
            style={{ color: "var(--ink)" }}
            data-testid="accept-invite-heading"
          >
            Choose a password.
          </h2>

          {checking && (
            <p className="mt-8 text-sm" style={{ color: "var(--muted)" }} data-testid="accept-invite-loading">
              Checking your invitation…
            </p>
          )}

          {complete ? (
            <div className="mt-8 border p-6" style={{ borderColor: "var(--border-strong)" }}>
              <CheckCircle2 size={24} style={{ color: "var(--accent)" }} />
              <p
                className="mt-4 text-base font-semibold"
                style={{ color: "var(--ink)" }}
                data-testid="accept-invite-confirmation"
              >
                Your account is active.
              </p>
              <p className="mt-2 text-sm leading-relaxed" style={{ color: "var(--muted)" }}>
                Sign in with your username and new password, then enter the 6-digit code we email you.
              </p>
              <Link
                to="/login"
                className="btn-primary mt-6 inline-flex items-center gap-2"
                data-testid="accept-invite-sign-in-link"
              >
                Continue To Sign In <KeyRound size={16} />
              </Link>
            </div>
          ) : (
            !checking && (
              <>
                {invite && (
                  <div
                    className="mt-6 border p-4 text-sm"
                    style={{ borderColor: "var(--border)", color: "var(--muted)" }}
                    data-testid="accept-invite-details"
                  >
                    <div style={{ color: "var(--ink)" }} className="font-semibold">
                      {invite.name} · {invite.company}
                    </div>
                    <div className="mt-1 font-mono text-xs">Username: {invite.username}</div>
                    <div className="mt-1 font-mono text-xs">{invite.work_email}</div>
                  </div>
                )}

                {error && (
                  <div
                    className="pill-danger mb-6 mt-6 flex items-start gap-2 p-3 text-sm"
                    data-testid="accept-invite-error"
                  >
                    <ShieldAlert size={16} className="mt-0.5" />
                    <span>{error}</span>
                  </div>
                )}

                <form onSubmit={submit} className="mt-8 space-y-8" data-testid="accept-invite-form">
                  <div>
                    <label className="field-label" htmlFor="invite-password">
                      New password
                    </label>
                    <PasswordField
                      id="invite-password"
                      value={newPassword}
                      onChange={(event) => setNewPassword(event.target.value)}
                      autoComplete="new-password"
                      minLength={12}
                      disabled={blocked}
                      required
                      testId="accept-invite-new-input"
                    />
                  </div>
                  <div>
                    <label className="field-label" htmlFor="invite-confirm">
                      Confirm new password
                    </label>
                    <PasswordField
                      id="invite-confirm"
                      value={confirmPassword}
                      onChange={(event) => setConfirmPassword(event.target.value)}
                      autoComplete="new-password"
                      minLength={12}
                      disabled={blocked}
                      required
                      testId="accept-invite-confirm-input"
                    />
                    <PasswordChecklist
                      password={newPassword}
                      confirm={confirmPassword}
                      personal={[invite?.name, invite?.username, invite?.work_email]}
                      testId="accept-invite-checklist"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={
                      loading ||
                      blocked ||
                      !isPasswordValid(newPassword, {
                        confirm: confirmPassword,
                        personal: [invite?.name, invite?.username, invite?.work_email],
                      })
                    }
                    className="btn-primary flex w-full items-center justify-center gap-2"
                    data-testid="accept-invite-submit-button"
                  >
                    {loading ? (
                      <Loader2 className="animate-spin" size={16} />
                    ) : (
                      <>
                        Activate Account <KeyRound size={16} />
                      </>
                    )}
                  </button>
                </form>
              </>
            )
          )}
        </motion.div>
      </main>
    </div>
  );
}
